import { Table } from '@tanstack/react-table';
import { useMemo } from 'react';

import { isInternalTableColumnId } from './table-column-ids';

export function getUserConfigurableTableColumns<TColumn extends { id: string }>(
  columns: TColumn[],
) {
  return columns.filter((column) => !isInternalTableColumnId(column.id));
}

export function orderColumnsByColumnOrder<TColumn extends { id: string }>(
  columns: TColumn[],
  columnOrder: string[],
) {
  return [...columns].sort((a, b) => {
    const aIndex = columnOrder.indexOf(a.id);
    const bIndex = columnOrder.indexOf(b.id);

    if (aIndex === -1 && bIndex === -1) return 0;
    if (aIndex === -1) return 1;
    if (bIndex === -1) return -1;

    return aIndex - bIndex;
  });
}

export function useGetTableSettingsStateFromTable<TData>(table: Table<TData>) {
  const allColumns = table.getAllColumns();
  const columnOrder = table.getState().columnOrder;
  const columnVisiblity = table.getState().columnVisibility;
  const columns = useMemo(
    () => getUserConfigurableTableColumns(allColumns),
    // We want column visiblity and order changes to trigger re-renders,
    // since we pull them from table APIs inside the following memos
    // oxlint-disable-next-line react/exhaustive-deps
    [allColumns, columnOrder, columnVisiblity],
  );

  const columnVisibility = useMemo(() => {
    const visible: typeof columns = [];
    const hidden: typeof columns = [];
    for (const column of columns) {
      if (column.getIsVisible()) {
        visible.push(column);
      } else {
        hidden.push(column);
      }
    }
    return {
      visible: orderColumnsByColumnOrder(visible, columnOrder),
      hidden: orderColumnsByColumnOrder(hidden, columnOrder),
    };
  }, [columnOrder, columns]);

  return useMemo(
    () => ({
      columns,
      visibleColumns: columnVisibility.visible,
      hiddenColumns: columnVisibility.hidden,
      columnVisibility,
      columnOrder,
    }),
    [columnOrder, columnVisibility, columns],
  );
}
